import { useCallback, useEffect, useMemo, useState } from "react";

// Preferencias de filtrado por ambito ("proyectos", "notion:<dbId>"...).
//
// Viven en localStorage y no en la base de datos a proposito: son preferencias
// de presentacion, del mismo tipo que la vista de Notion o la escala de la
// cronologia, y no merecen un viaje al servidor ni una migracion.
//
//   visibles   nombres de los filtros que el usuario quiere ver en la barra
//   recordar   si al volver se restauran los ultimos filtros usados
//   porDefecto con que filtros arranca la pagina
//   ultimos    lo que habia puesto la ultima vez (solo si `recordar`)
const CLAVE = (ambito) => `filtros.${ambito}`;

function leer(ambito) {
  try {
    const raw = localStorage.getItem(CLAVE(ambito));
    const p = raw ? JSON.parse(raw) : {};
    return (p && typeof p === "object") ? p : {};
  } catch { return {}; }
}
function escribir(ambito, prefs) {
  try { localStorage.setItem(CLAVE(ambito), JSON.stringify(prefs)); } catch { /* modo privado */ }
}

// Un filtro vacio es [] para los de varias opciones y "" para texto, fechas y
// conmutadores —donde "" es apagado y "1" encendido, para que viaje igual que el
// resto y no haya que tratar booleanos aparte al guardarlo en localStorage—.
export function valoresVacios(campos) {
  const v = {};
  for (const c of campos) {
    if (c.tipo === "texto" || c.tipo === "conmutador") v[c.key] = "";
    else if (c.tipo === "rango") { v[c.desde] = ""; v[c.hasta] = ""; }
    else v[c.key] = [];
  }
  return v;
}

// Solo se guardan las claves que el ambito conoce: si mañana desaparece un
// filtro, su valor no se arrastra para siempre en el navegador de nadie.
function saneaValores(campos, valores) {
  const vacios = valoresVacios(campos);
  const out = { ...vacios };
  for (const k of Object.keys(vacios)) {
    const v = valores?.[k];
    if (Array.isArray(vacios[k])) out[k] = Array.isArray(v) ? v.map(String) : [];
    else out[k] = typeof v === "string" ? v : "";
  }
  return out;
}

export function hayAlgunFiltro(valores) {
  return Object.values(valores || {}).some(v => (Array.isArray(v) ? v.length > 0 : !!v));
}

export function cuentaFiltros(valores) {
  return Object.values(valores || {})
    .reduce((n, v) => n + (Array.isArray(v) ? v.length : (v ? 1 : 0)), 0);
}

// `inicial` son los filtros con los que arranca la pagina mientras el usuario
// no haya guardado los suyos (el gestor, por ejemplo, ensena solo los activos).
export function usePrefsFiltros(ambito, campos, inicial = null) {
  const vacios = useMemo(() => valoresVacios(campos), [campos]);
  const arranque = useMemo(() => ({ ...vacios, ...(inicial || {}) }), [vacios, inicial]);

  const departida = useCallback((p) => saneaValores(campos, {
    ...arranque,
    ...(p.porDefecto || {}),
    ...(p.recordar ? p.ultimos || {} : {}),
  }), [campos, arranque]);

  const [prefs, setPrefs] = useState(() => leer(ambito));
  const [valores, setValores] = useState(() => departida(leer(ambito)));

  // Al cambiar de ambito (otra base de datos de Notion, por ejemplo) se releen
  // las preferencias de ese ambito y se recalculan los valores de partida.
  useEffect(() => {
    const p = leer(ambito);
    setPrefs(p);
    setValores(departida(p));
  }, [ambito]); // eslint-disable-line

  // Los ultimos filtros se apuntan en cuanto cambian, pero solo si el usuario
  // ha pedido que se recuerden.
  useEffect(() => {
    if (!prefs.recordar) return;
    escribir(ambito, { ...leer(ambito), ultimos: valores });
  }, [ambito, valores, prefs.recordar]);

  const guardaPrefs = useCallback((patch) => {
    setPrefs(prev => {
      const next = { ...prev, ...patch };
      escribir(ambito, next);
      return next;
    });
  }, [ambito]);

  const setValor = useCallback((key, v) => setValores(prev => ({ ...prev, [key]: v })), []);

  // Filtros que se pintan. Sin preferencia guardada se ven los que el ambito
  // marca como `porDefectoVisible !== false`.
  const visibles = useMemo(() => {
    const guardados = Array.isArray(prefs.visibles) ? prefs.visibles : null;
    return campos
      .filter(c => (guardados ? guardados.includes(c.key) : c.porDefectoVisible !== false))
      .map(c => c.key);
  }, [prefs.visibles, campos]);

  const setVisibles = useCallback((lista) => guardaPrefs({ visibles: lista }), [guardaPrefs]);
  const setRecordar = useCallback((on) => {
    guardaPrefs({ recordar: !!on, ...(on ? {} : { ultimos: undefined }) });
  }, [guardaPrefs]);

  const guardarPorDefecto = useCallback(() => guardaPrefs({ porDefecto: valores }), [guardaPrefs, valores]);
  const olvidarPorDefecto = useCallback(() => guardaPrefs({ porDefecto: undefined }), [guardaPrefs]);

  const limpiar    = useCallback(() => setValores(vacios), [vacios]);
  const porDefecto = useCallback(
    () => setValores(saneaValores(campos, { ...arranque, ...(prefs.porDefecto || {}) })),
    [campos, arranque, prefs.porDefecto]);

  // Deja el ambito como recien instalado: ni filtros visibles a medida, ni
  // valores por defecto, ni memoria.
  const restablecer = useCallback(() => {
    try { localStorage.removeItem(CLAVE(ambito)); } catch { /* modo privado */ }
    setPrefs({});
    setValores(saneaValores(campos, arranque));
  }, [ambito, campos, arranque]);

  // Un filtro que se oculta deja de aplicarse: si no se ve, no puede explicar
  // por que faltan filas.
  useEffect(() => {
    setValores(prev => {
      let cambia = false;
      const next = { ...prev };
      for (const c of campos) {
        if (visibles.includes(c.key)) continue;
        const claves = c.tipo === "rango" ? [c.desde, c.hasta] : [c.key];
        for (const k of claves) {
          const vacio = Array.isArray(vacios[k]) ? [] : "";
          const estaVacio = Array.isArray(prev[k]) ? prev[k].length === 0 : !prev[k];
          if (!estaVacio) { next[k] = vacio; cambia = true; }
        }
      }
      return cambia ? next : prev;
    });
  }, [visibles, campos, vacios]);

  return {
    valores, setValor, setValores,
    visibles, setVisibles,
    recordar: !!prefs.recordar, setRecordar,
    tienePorDefecto: !!prefs.porDefecto,
    guardarPorDefecto, olvidarPorDefecto, porDefecto,
    limpiar, restablecer,
    hayFiltro: hayAlgunFiltro(valores),
  };
}
